export const SERVER_STATUSES = [
  "NEW", "CONNECTING", "PROVISIONING", "TESTING", "READY", "ACTIVE", "DRAINING", "MAINTENANCE", "FAILED", "RETIRED",
] as const;
export type ServerStatus = (typeof SERVER_STATUSES)[number];

export const SERVER_HEALTH = ["UNKNOWN", "HEALTHY", "WARNING", "OFFLINE"] as const;
export type ServerHealth = (typeof SERVER_HEALTH)[number];

/** Spec-level status shown to admins: lifecycle status, overridden by WARNING/OFFLINE health for live servers. */
export type ServerDisplayStatus = ServerStatus | "WARNING" | "OFFLINE";

const LIVE: readonly ServerStatus[] = ["READY", "ACTIVE", "DRAINING", "MAINTENANCE"];
export function displayStatus(status: ServerStatus, health: ServerHealth): ServerDisplayStatus {
  if (LIVE.includes(status) && (health === "OFFLINE" || health === "WARNING")) return health;
  return status;
}

export const PROVISIONING_STEPS = [
  { key: "CONNECT", name: "Connect" },
  { key: "VALIDATE_OS", name: "Validate OS" },
  { key: "SYSTEM_UPDATE", name: "System update" },
  { key: "SECURITY_BASELINE", name: "Security baseline" },
  { key: "FIREWALL", name: "Firewall" },
  { key: "HOSTDOCK_AGENT", name: "HostDock Agent" },
  { key: "DOCKER", name: "Docker" },
  { key: "NETWORKING", name: "Networking" },
  { key: "REVERSE_PROXY", name: "Reverse proxy" },
  { key: "PHP_SUPPORT", name: "PHP support" },
  { key: "NODEJS_SUPPORT", name: "Node.js support" },
  { key: "PYTHON_SUPPORT", name: "Python support" },
  { key: "MARIADB", name: "MariaDB" },
  { key: "POSTGRESQL", name: "PostgreSQL" },
  { key: "REDIS", name: "Redis" },
  { key: "MONGODB", name: "MongoDB" },
  { key: "PHPMYADMIN", name: "phpMyAdmin" },
  { key: "SFTP", name: "SFTP" },
  { key: "DNS_SERVER", name: "DNS server" },
  { key: "MONITORING", name: "Monitoring" },
  { key: "LOGGING", name: "Logging" },
  { key: "BACKUP_AGENT", name: "Backup agent" },
  { key: "CLAMAV", name: "Malware scanning" },
  { key: "MAIL", name: "Email hosting" },
  { key: "PRODUCTION_TESTS", name: "Production tests" },
  { key: "READY", name: "Ready" },
] as const;
export type ProvisioningStepKey = (typeof PROVISIONING_STEPS)[number]["key"];
export type ProvisioningStepStatus = "PENDING" | "RUNNING" | "SUCCESS" | "FAILED" | "SKIPPED";
export type ProvisioningJobStatus = "PENDING" | "RUNNING" | "SUCCESS" | "FAILED" | "CANCELLED";

/** Node-level services the agent reports on. */
export const NODE_SERVICES = [
  "agent", "docker", "proxy", "mariadb", "postgresql", "redis", "mongodb", "phpmyadmin", "sftp", "monitoring", "logging", "backup",
] as const;
export type NodeService = (typeof NODE_SERVICES)[number];
export type ServiceStatus = "RUNNING" | "STOPPED" | "FAILED" | "NOT_INSTALLED" | "UNKNOWN";

export const READINESS_CHECKS = [
  "os", "kernel", "dns", "time_sync", "firewall", "agent", "docker", "docker_network", "reverse_proxy", "ssl_acme",
  "php_container", "nodejs_container", "python_container", "mariadb", "postgresql", "redis", "mongodb", "filesystem", "sftp",
  "monitoring", "logging", "backup_connectivity", "http_app", "resource_limits", "tenant_isolation",
] as const;
export type ReadinessCheck = (typeof READINESS_CHECKS)[number];
export type ReadinessStatus = "PASS" | "FAIL" | "SKIP";

export const AGENT_COMMANDS = [
  "READINESS", "LOGS", "REBOOT",
  "SITE_ENSURE", "SITE_REMOVE", "WORKLOAD_APPLY", "WORKLOAD_REMOVE", "WORKLOAD_ACTION", "PROXY_SYNC", "HTTP_CHECK",
  "DB_CREATE", "DB_DROP", "DB_USER_PASSWORD", "DB_SIZES", "DB_POOL_REMOVE", "REDIS_ACL_SET", "REDIS_ACL_DEL", "SITE_LOGS",
  "FILE_OP", "SFTP_SYNC", "CRON_RUN", "GIT_SYNC", "PMA_TOKEN",
  "WP_CLI", "APP_EXEC", "SITE_COPY", "DB_COPY", "SNAPSHOT_CREATE", "SNAPSHOT_RESTORE", "SNAPSHOT_DELETE",
  "PACKAGE_APPLY",
  "ARCHIVE_OP", "TRANSFER_HTTP", "TRANSFER_MULTIPART", "TRANSFER_PULL", "DB_EXPORT", "DB_IMPORT", "DB_REMOTE_DUMP", "DB_TRANSFER_CLEANUP",
  "MAIL_SYNC", "MAIL_HEALTH", "MAIL_WEBMAIL_TOKEN", "MAIL_IMPORT",
  "SECURITY_SCAN", "DOCKSHIELD_SCAN", "QUARANTINE_OP",
  "DNS_SYNC",
] as const;
export type AgentCommandType = (typeof AGENT_COMMANDS)[number];
export type AgentCommandStatus = "PENDING" | "DISPATCHED" | "SUCCESS" | "FAILED" | "EXPIRED";

/**
 * Runtime versions in numeric order, oldest first, without duplicates ("8.10" after "8.9"). The last entry is the
 * default for new websites, so the order must never depend on how an admin typed the list.
 */
export function sortRuntimeVersions(versions: readonly string[]): string[] {
  const key = (v: string) => v.split(".").map((n) => Number(n) || 0);
  return [...new Set(versions)].sort((a, b) => {
    const x = key(a);
    const y = key(b);
    for (let i = 0; i < Math.max(x.length, y.length); i++) if ((x[i] ?? 0) !== (y[i] ?? 0)) return (x[i] ?? 0) - (y[i] ?? 0);
    return 0;
  });
}

export interface RuntimeCatalogue {
  php: string[];
  node: string[];
  python: string[];
}

/** Wire format sent by the agent on every heartbeat. */
export interface AgentHeartbeat {
  version: string;
  system: {
    hostname: string;
    osName: string;
    osVersion: string;
    kernel: string;
    arch: string;
    cpuCores: number;
    uptimeSeconds: number;
  };
  metrics: {
    collectedAt: string;
    cpuPercent: number;
    load1: number;
    load5: number;
    load15: number;
    memTotal: number;
    memUsed: number;
    diskTotal: number;
    diskUsed: number;
    inodesTotal: number;
    inodesUsed: number;
    diskReadBps: number;
    diskWriteBps: number;
    netRxBps: number;
    netTxBps: number;
    containersRunning: number;
    containersTotal: number;
  };
  services: { service: NodeService; status: ServiceStatus; detail?: string }[];
}

export interface ReadinessResult {
  checks: { check: ReadinessCheck; status: ReadinessStatus; detail: string }[];
}
